import React from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

/**
 * Interface pour les props du composant AlertDialog
 */
interface Props {
  /** Titre du dialogue */
  title: string;
  /** Contenu du dialogue */
  content: string;
  /** État d'ouverture du dialogue */
  open: boolean;
  /** Fonction appelée lors de l'acceptation */
  accept: () => any;
  /** Fonction appelée lors de l'annulation (optionnelle) */
  cancel?: () => any;
}

/**
 * Composant de dialogue d'alerte avec confirmation
 * 
 * Ce composant affiche :
 * - Un dialogue modal avec titre et contenu
 * - Boutons d'action (Accepter et optionnellement Annuler)
 * - Support des sauts de ligne dans le texte
 * - Gestion des événements de fermeture
 * 
 * Logique métier :
 * - Affichage conditionnel du bouton Annuler
 * - Support des sauts de ligne avec whiteSpace: "pre-line"
 * - Bouton Accepter toujours présent
 * - Bouton Annuler avec focus automatique si présent
 * 
 * @param accept Fonction appelée lors de l'acceptation
 * @param cancel Fonction appelée lors de l'annulation (optionnelle)
 * @param props Autres props du dialogue (title, content, open)
 * @returns Composant React du dialogue d'alerte
 */
export const AlertDialog: React.FC<Props> = ({ accept, cancel, ...props }) => {
  return (
    <Dialog
      open={props.open}
      onClose={cancel}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title"><span style={{ whiteSpace: "pre-line" }}>{props.title}</span></DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <span style={{ whiteSpace: "pre-line" }}>{props.content}</span>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {cancel &&
          <Button
            onClick={cancel}
            color="secondary"
            variant={'contained'}
            autoFocus
          >
            Annuler
          </Button>
        }
        <Button onClick={accept} color="primary" variant={'contained'}>
          Accepter
        </Button>
      </DialogActions>
    </Dialog>
  );
};
